import axios from "axios";
import { authState, keycloak, getAuthBootstrap } from "@/plugins/keycloak";
import { configService } from "@/infrastructure/services/ConfigService";
import { logger } from "@/infrastructure/services/LoggerService";
import { updateActivity, checkSessionValidity, clearSession } from "@/shared/utils/sessionManager";
import { clearListBrowsingState } from "@/shared/utils/listBrowsingStorage";
/**
 * Path fragment for the auth bootstrap endpoint. The request gate skips this
 * URL because the bootstrap call itself is what resolves the
 * `authBootstrap.awaitInitial()` promise — gating it would deadlock.
 */
const BOOTSTRAP_PATH = "/auth/bootstrap";
/** Create an Axios instance with default settings */
const api = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
});
/**
 * Neutralise the JSON default above for file uploads.
 *
 * Axios' `transformRequest` reacts to a JSON content type by running `formDataToJSON()` over a
 * `FormData` body: the request then carries the string `{"file":{"name":…,"type":…}}` — metadata
 * only, the bytes never leave the browser — and the server rejects it with 415. Clearing the header
 * here lets the adapter set `multipart/form-data` with the boundary only the sender can produce.
 *
 * Done centrally because the per-call-site override is invisible at the point of OMISSION and fails
 * silently: six upload adapters had each rediscovered it independently, and a seventh (the consent
 * proof) still shipped broken. Callers now need no upload-specific header at all.
 */
api.interceptors.request.use((config) => {
    if (config.data instanceof FormData) {
        config.headers.setContentType(false);
    }
    return config;
});
/** Request Interceptor to include the Keycloak token and set base URL */
api.interceptors.request.use(async (config) => {
    // Gate every business call on the auth-bootstrap initial promise so that
    // the user's default roles are materialized in Keycloak before any
    // downstream API hits a 401/403. Skip the bootstrap call itself —
    // gating it would deadlock against its own resolution.
    const isBootstrap = config.url?.includes(BOOTSTRAP_PATH) ?? false;
    if (!isBootstrap) {
        await getAuthBootstrap().awaitInitial();
    }
    if (!config.baseURL) {
        try {
            const apiConfig = configService.api;
            if (apiConfig?.bffUrl) {
                config.baseURL = apiConfig.bffUrl;
            }
            else {
                logger.debug("BFF base URL not available from ConfigService");
                throw new Error("Base URL not set");
            }
        }
        catch (error) {
            logger.debug("Failed to get API base URL from ConfigService:", error);
            // Original error is logged above; ts lib (ES2020) doesn't expose ErrorOptions for `cause`.
            // eslint-disable-next-line preserve-caught-error
            throw new Error("Base URL not set");
        }
    }
    if (authState.isAuthenticated && keycloak.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`;
        updateActivity();
    }
    return config;
}, (error) => Promise.reject(error));
/** Response Interceptor to handle token refresh on 401/403 */
api.interceptors.response.use((response) => {
    // Unwrap only when the caller opted in. An absent flag means "hand me the body untouched",
    // which is what the envelope-reading call sites depend on (see `unwrapEnvelope`'s doc above).
    if (response.config.unwrapEnvelope) {
        // Unwrap BFF ApiResponse<T> envelope { success: boolean, data: T, message: string | null }
        const body = response.data;
        if (body !== null &&
            typeof body === "object" &&
            typeof body.success === "boolean" &&
            Object.prototype.hasOwnProperty.call(body, "data")) {
            // Paginated responses carry a separate "pagination" object alongside "data"
            if (body.pagination != null && typeof body.pagination === "object") {
                response.data = {
                    items: body.data,
                    page: body.pagination.page,
                    pageSize: body.pagination.perPage,
                    totalCount: body.pagination.totalItems,
                    totalPages: body.pagination.totalPages,
                };
            }
            else {
                response.data = body.data;
            }
        }
    }
    return response;
}, async (error) => {
    const originalRequest = error.config;
    // Auth-bootstrap recovery branch: when a business call fails with 401/403
    // and the bootstrap state is anything other than "succeeded", force a
    // full token refresh via authBootstrap.recover() then retry the original
    // request once. The per-request `_recoveryAttempted` flag prevents loops.
    // Runs BEFORE the existing updateToken(30) path so we subsume the
    // lightweight refresh when the bootstrap hasn't yet completed.
    const status = error.response?.status;
    if (status === 401 || status === 403) {
        const svc = getAuthBootstrap();
        if (originalRequest &&
            !originalRequest._recoveryAttempted &&
            svc.getState() !== "succeeded") {
            originalRequest._recoveryAttempted = true;
            await svc.recover();
            return api.request(originalRequest);
        }
    }
    if ((error.response?.status === 401 || error.response?.status === 403) &&
        !originalRequest._retryAttempted) {
        originalRequest._retryAttempted = true;
        const sessionState = checkSessionValidity();
        if (!sessionState.isValid) {
            logger.debug("Session expired on API call, redirecting to session-ended", {
                status: error.response?.status,
                reason: sessionState.reason,
            });
            authState.isAuthenticated = false;
            clearSession();
            clearListBrowsingState();
            if (window.location.pathname !== "/session-ended") {
                window.location.href = "/session-ended";
            }
            return Promise.reject(error);
        }
        try {
            await keycloak.updateToken(30);
            logger.debug("Token refreshed successfully after " + error.response?.status);
            originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
            return api.request(originalRequest);
        }
        catch (refreshError) {
            logger.debug("Token refresh failed on API " +
                error.response?.status +
                ", redirecting to session-ended", refreshError);
            authState.isAuthenticated = false;
            clearSession();
            clearListBrowsingState();
            if (window.location.pathname !== "/session-ended") {
                window.location.href = "/session-ended";
            }
            return Promise.reject(refreshError);
        }
    }
    return Promise.reject(error);
});
export default api;
